'use strict';

var _ = require('lodash');

/**
 * Market Analysis - Market Product tab - Charts
 */
var MarketProductGroupTransformerFactory = function MarketProductGroupTransformerFactory($filter, DataTransformer, effectiveValueService, momentService, mergeScenarioDataService, metricReferenceDataService) {
  var rmMomentFormat = $filter('rmMomentFormat');
  function MarketProductGroupTransformer(targetType, priority) {
    DataTransformer.call(this);
    this.sourceType = 'market-product-group';
    this.targetType = targetType;
    this.priority = priority || 0;
  }
  function getDaysInMonth(year, month) {
    var isLeap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    return [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
  }

  // prepares a map of revenue day plan values for a given market for a given month
  function createTargetDataMap(data, marketDemands) {
    if (!marketDemands || marketDemands.length === 0) {
      return;
    }
    var dayMarketRecords = {};
    _.forEach(marketDemands, function (entry) {
      var date = entry.departureDate;
      if (_.has(dayMarketRecords, [date, entry.origin, entry.destination, entry.posName, 'quantity'])) {
        dayMarketRecords[date][entry.origin][entry.destination][entry.posName].quantity++;
      } else {
        _.set(dayMarketRecords, [date, entry.origin, entry.destination, entry.posName, 'quantity'], 1);
      }
    });
    _.forEach(data.target, function (d) {
      var daysInMonth = getDaysInMonth(d.year, d.month);
      var dayTarget = d.targetValue / daysInMonth;
      for (var i = 1; i <= daysInMonth; i++) {
        var date = momentService.of(d.year + '-' + d.month + '-' + i, momentService.FIELD.DEPARTURE_DATE, momentService.FORMAT.DATE_ISO);
        var dateString = rmMomentFormat(date, 'DATE_ISO');
        if (!_.has(dayMarketRecords, [dateString, d.marketOrigin, d.marketDestination, d.spos])) {
          continue;
        }
        var recordsQuantity = dayMarketRecords[dateString][d.marketOrigin][d.marketDestination][d.spos].quantity;
        dayMarketRecords[dateString][d.marketOrigin][d.marketDestination][d.spos][d.targetType] = dayTarget / recordsQuantity;
      }
    });
    return dayMarketRecords;
  }
  MarketProductGroupTransformer.prototype = Object.create(DataTransformer.prototype);
  MarketProductGroupTransformer.constructor = MarketProductGroupTransformer;
  MarketProductGroupTransformer.prototype.canTransform = function (sourceType) {
    return this.sourceType === sourceType;
  };
  MarketProductGroupTransformer.prototype.isCacheable = function () {
    return true;
  };
  MarketProductGroupTransformer.prototype.getCacheKey = function (metrics, metricDescriptor) {
    var cacheKeys = [this.sourceType, metrics, metricDescriptor.dimension, _.get(metricDescriptor, 'params.selectedReference')];
    return _.chain(cacheKeys).flatten().join('-').value();
  };
  function setEffectiveValues(result, entry) {
    var effective = effectiveValueService.calculateAll(entry, 'market-product-group-analysis');
    _.assign(result, effective);
  }
  var getDimensionedData = function getDimensionedData(entry, date, memoizedDOWOfMoment, memoizedYearMonthDayOfMoment, dimension, selectedReference) {
    var flowTypePrefix = dimension || 'regular';
    var flowTypeKey = flowTypePrefix + 'Metrics';
    var entryKey = entry.carrier + entry.market + entry.posName + entry.classGroup + entry.departureDate;
    var newEntry = {
      entryKey: entryKey,
      entity: entry.entity,
      dowGroup: entry.dowGroup,
      carrier: entry.carrier,
      classGroup: entry.classGroup,
      origin: entry.origin,
      destination: entry.destination,
      season: entry.season,
      seasonOverride: entry.userMappedSeasonOverride || entry.userRegularSeasonOverride,
      market: entry.market,
      posName: entry.posName,
      departureDate: memoizedYearMonthDayOfMoment(date),
      departureDateMoment: date,
      departureDateMomentDDD: memoizedDOWOfMoment(date),
      marketDemandId: entry.marketDemandId,
      referenceCurveMarketDemandId: entry.referenceCurveMarketDemandId,
      daysToDeparture: entry.daysToDeparture,
      regularProfileLevel: entry.regularProfileLevel,
      mappedProfileLevel: entry.mappedProfileLevel
    };

    //calc effective field and assign it to entry
    setEffectiveValues(entry, entry);
    var flowTypeMetrics = entry[flowTypeKey];
    var metricsData = metricReferenceDataService.getMetricsData(['resHoldTotal', 'revenue', 'yield', 'revenuePaxDistance'], flowTypeMetrics, selectedReference);
    if (flowTypePrefix === 'regular') {
      newEntry.unadjustedDemand = entry.regularRemainingDemandFC;
      newEntry.adjustedDemand = entry.effectiveRegularSvcRemainingDemandFC;
      newEntry.bookingCurve = entry.bookingCurve;
      newEntry.revenueCurve = entry.revenueCurve;
      newEntry.lastYearBookingCurve = entry.lastYearBookingCurve;
      newEntry.lastYearRevenueCurve = entry.lastYearRevenueCurve;
    } else {
      newEntry.unadjustedDemand = entry.mappedRemainingDemandFC;
      newEntry.adjustedDemand = entry.effectiveMappedSvcRemainingDemandFC;
      newEntry.bookingCurve = entry.mappedBookingCurve;
      newEntry.revenueCurve = entry.mappedRevenueCurve;
      newEntry.lastYearBookingCurve = entry.mappedLastYearBookingCurve;
      newEntry.lastYearRevenueCurve = entry.mappedLastYearRevenueCurve;
    }
    return _.merge(newEntry, metricsData);
  };
  MarketProductGroupTransformer.prototype.transform = function (data, metrics, metricDescriptor) {
    var selectedReference = _.get(metricDescriptor, 'params.selectedReference');
    var dimension = metricDescriptor.dimension;
    var metricsKeys = [];
    var mlength = metrics.length;
    for (var m = 0; m < mlength; m++) {
      metricsKeys[m] = 'm' + (m + 1);
    }
    var memoizedMomentOfDepartureDate = _.memoize(getMomentOfDate);
    var memoizedDOWOfMoment = _.memoize(getDOWOfMoment, function (moment) {
      return moment.valueOf();
    });
    var memoizedYearMonthDayOfMoment = _.memoize(getYearMonthDayOfMoment, function (moment) {
      return moment.valueOf();
    });
    function transformData(marketDemand) {
      return _.transform(marketDemand, function (result, entry) {
        var date = memoizedMomentOfDepartureDate(entry.departureDate, momentService.FIELD.DEPARTURE_DATE);
        var newEntry = getDimensionedData(entry, date, memoizedDOWOfMoment, memoizedYearMonthDayOfMoment, dimension, selectedReference);
        for (m = 0; m < mlength; m++) {
          var curMetricName = metrics[m];
          newEntry[metricsKeys[m]] = newEntry[curMetricName];
        }
        return result.push(newEntry);
      });
    }
    var productionData = transformData(data.domain.flattenedAggregatedMarketDemand);
    var scenarioData = transformData(data.thirdDomain && data.thirdDomain.flattenedAggregatedMarketDemand || []);
    var results = mergeScenarioDataService.merge(productionData, scenarioData);
    var targetDataMap = createTargetDataMap(data.secondDomain, results);
    for (var i = 0, length = results.length; i < length; i++) {
      var entry = results[i];
      entry.yieldPlan = targetDataMap[entry.departureDate][entry.origin][entry.destination][entry.posName].Yield;
      entry.bookingsPlan = targetDataMap[entry.departureDate][entry.origin][entry.destination][entry.posName].Bookings;
      entry.revenuePlan = targetDataMap[entry.departureDate][entry.origin][entry.destination][entry.posName].Revenue;
    }
    return results;
  };
  function getMomentOfDate(date, field) {
    return momentService.of(date, field);
  }
  function getDOWOfMoment(moment) {
    return rmMomentFormat(moment, 'DAY_OF_WEEK');
  }
  function getYearMonthDayOfMoment(moment) {
    return rmMomentFormat(moment, 'DATE_ISO');
  }
  return MarketProductGroupTransformer;
};
MarketProductGroupTransformerFactory.$inject = ['$filter', 'DataTransformerFactory', 'CalculateEffectiveValueService', 'MomentService', 'MergeScenarioDataService', 'MetricReferenceDataService'];
module.exports = MarketProductGroupTransformerFactory;